import { useCallback, useEffect, useRef, useState } from "react";
import {
    ArrowUp,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    HelpCircle,
    Home,
    Loader2,
    MessageCircle,
    MessageSquareText,
    MoreHorizontal,
    Search,
    Send,
    SendHorizontal,
    Smile,
    Volume2,
    VolumeX,
    X,
} from "lucide-react";
import logo from "assets/img/logo.png";
import { SUPPORT_AGENTS, supportAgent } from "./agents";
import HelpMarkdown from "./HelpMarkdown";
import {
    createTicket,
    getHelpArticle,
    getHelpCenter,
    getHelpCollection,
    getMyTicket,
    getMyTickets,
    getMyUnreadCount,
    getSupportHome,
    HelpArticleFull,
    HelpArticleRef,
    HelpCollectionSummary,
    searchHelp,
    sendTicketMessage,
    supportErrorMessage,
    SupportHome,
    SupportTicket,
    SupportTicketSummary,
} from "interfaces/support";

/** Launcher: Intercom's bubble silhouette (tall rounded square, tail running down the right edge), plain — no smile. */
function LauncherIcon() {
    return (
        <svg viewBox="0 0 28 26" className="h-6 w-6" aria-hidden>
            <path
                fill="currentColor"
                d="M28 26s-4.714-1.855-8.527-3.34H3.437C1.54 22.66 0 21.026 0 19.013V3.644C0 1.633 1.54 0 3.437 0h21.125c1.898 0 3.437 1.632 3.437 3.645v12.404H28V26Z"
            />
        </svg>
    );
}

/**
 * Support messenger, bottom right of the app and of the login / sign-up pages (replaced the WhatsApp
 * pop-up). Three tabs — Accueil, Messages, Aide (the help center written in /bo/help-center) — and a
 * conversation screen over them. Every message lands in /bo/support and on Discord; our replies come
 * back here and by email. `visitor`: no session expected (auth pages) — the visitor gives an email
 * with their first message.
 */

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VISITOR_EMAIL_KEY = "support_visitor_email";
const UNREAD_POLL_MS = 60_000;
const TICKET_POLL_MS = 15_000;
const SOUND_MUTED_KEY = "support_sound_muted";
const POWERED_BY_URL = "https://himasolutions.fr/";
const EMOJIS = ["😀", "😊", "😂", "😅", "😉", "😍", "🙏", "👍", "👋", "👏", "🙌", "💪", "🎉", "🔥", "✅", "❌", "❤️", "🤔", "😕", "😢", "😮", "👀", "💡", "🚀"];
// Support hours, Paris time: Monday to Friday, 9h–17h (public holidays not handled)
const SUPPORT_OPEN_HOUR = 9;
const SUPPORT_CLOSE_HOUR = 17;
// `?support=new` opens a new conversation (help articles link to it)
const NEW_CONVERSATION_PARAM = "new";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Chime played when a reply from us arrives. Synthesized with the Web Audio API (no asset to ship).
 * Browsers only let a page make sound after a user gesture, so the context is created on the first
 * click / key press anywhere on the page; before that, a reply arrives silently.
 */
let audioCtx: AudioContext | null = null;

function unlockAudio() {
    try {
        if (!audioCtx) audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        if (audioCtx.state === "suspended") audioCtx.resume();
    } catch {
        // No Web Audio: stay silent
    }
}

function playChime() {
    const ctx = audioCtx;
    if (!ctx || ctx.state !== "running") return;
    const now = ctx.currentTime;
    // Two soft notes, a fifth apart
    [
        [880, 0],
        [1318.5, 0.12],
    ].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.18, now + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + delay + 0.4);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + delay);
        osc.stop(now + delay + 0.45);
    });
}

const adminMessages = (t: SupportTicket | null) => (t ? t.messages.filter((m) => m.from === "admin").length : 0);

type Tab = "home" | "messages" | "help";
type Conversation = { kind: "new" } | { kind: "ticket"; id: string };
type HelpView = { kind: "index" } | { kind: "collection"; id: string } | { kind: "article"; id: string };

/** « À l'instant », « il y a 5 min », « il y a 2 h », then the date. */
function relativeTime(d: string) {
    const minutes = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const readLocal = (key: string) => {
    try {
        return localStorage.getItem(key) ?? "";
    } catch {
        return "";
    }
};

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? "s" : ""}`;

/**
 * Is the support online now, and if not, when it answers next (« ce matin », « demain matin »,
 * « lundi matin »). Read in Europe/Paris whatever the visitor's own time zone.
 */
function supportAvailability(now = new Date()): { online: boolean; backAt: string; onDuty: string[] } {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Paris", weekday: "short", hour: "numeric", hourCycle: "h23" }).formatToParts(now);
    const day = WEEKDAYS.indexOf(parts.find((p) => p.type === "weekday")?.value ?? ""); // 0 = Monday
    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const workday = day >= 0 && day < 5;
    const online = workday && hour >= SUPPORT_OPEN_HOUR && hour < SUPPORT_CLOSE_HOUR;
    const when = workday && hour < SUPPORT_OPEN_HOUR ? "ce matin" : day < 4 || day === 6 ? "demain matin" : "lundi matin";
    // Agents on duty right now (each has their own days, same hours)
    const onDuty = SUPPORT_AGENTS.filter((a) => a.days.includes(day) && hour >= SUPPORT_OPEN_HOUR && hour < SUPPORT_CLOSE_HOUR).map((a) => a.key);
    return { online, backAt: `${when} à ${SUPPORT_OPEN_HOUR}h`, onDuty };
}

/** Re-read every minute, so the status flips at 9h and 17h while the widget stays open. */
function useSupportAvailability() {
    const [availability, setAvailability] = useState(() => supportAvailability());
    useEffect(() => {
        const timer = setInterval(() => setAvailability(supportAvailability()), 60_000);
        return () => clearInterval(timer);
    }, []);
    return availability;
}

const offlineNotice = (backAt: string) =>
    `Nous sommes hors ligne. Le support reviendra vers vous ${backAt} (du lundi au vendredi, de ${SUPPORT_OPEN_HOUR}h à ${SUPPORT_CLOSE_HOUR}h).`;

/** The team's photos overlapping, each with its status dot (green on duty, orange off duty). */
function TeamAvatars({ size = "md", onDuty, ring = "ring-surface" }: { size?: "md" | "lg"; onDuty: string[]; ring?: string }) {
    const box = size === "lg" ? "h-10 w-10" : "h-8 w-8";
    return (
        <div className={`flex shrink-0 ${size === "lg" ? "-space-x-1.5" : "-space-x-2.5"}`}>
            {SUPPORT_AGENTS.map((member) => {
                const available = onDuty.includes(member.key);
                return (
                    <span key={member.key} title={`${member.name} · ${available ? "disponible" : "indisponible"}`} className={`relative ${box}`}>
                        <img src={member.photo} alt={member.name} className={`h-full w-full rounded-full object-cover ring-2 ${ring}`} />
                        <span
                            aria-label={available ? "Disponible" : "Indisponible"}
                            className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ${ring} ${available ? "bg-success" : "bg-warning"}`}
                        />
                    </span>
                );
            })}
        </div>
    );
}

/** The support's photo; `online` adds the status dot (green online, orange offline). */
function Avatar({ size = "md", online, ring = "ring-surface", agent }: { size?: "xs" | "sm" | "md" | "lg"; online?: boolean; ring?: string; agent?: string }) {
    const box = size === "lg" ? "h-10 w-10" : size === "md" ? "h-8 w-8" : size === "sm" ? "h-7 w-7" : "h-6 w-6";
    const who = supportAgent(agent);
    return (
        <span className={`relative shrink-0 ${box}`}>
            <img src={who.photo} alt={who.name} className="h-full w-full rounded-full object-cover" />
            {online !== undefined && (
                <span
                    aria-label={online ? "En ligne" : "Hors ligne"}
                    className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ${ring} ${online ? "bg-success" : "bg-warning"}`}
                />
            )}
        </span>
    );
}

function PoweredBy() {
    return (
        <div className="pb-2 pt-1 text-center">
            <a href={POWERED_BY_URL} target="_blank" rel="noopener noreferrer" className="text-xs text-ink-faint transition-colors hover:text-ink-muted">
                Powered by Hima Solutions
            </a>
        </div>
    );
}

/** Centered title bar of the Messages and Aide tabs. */
function TabHeader({ title, onBack, onClose }: { title: string; onBack?: () => void; onClose: () => void }) {
    return (
        <div className="flex items-center px-3 py-2.5">
            <div className="w-9">
                {onBack && (
                    <button type="button" onClick={onBack} aria-label="Retour" className="rounded-lg p-1.5 text-ink hover:bg-gray-100">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                )}
            </div>
            <p className="flex-1 text-center text-base font-semibold text-ink">{title}</p>
            <div className="w-9">
                <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-ink-muted hover:bg-gray-100 hover:text-ink">
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

function HelpSearchBox({ value, onChange, onFocus, autoFocus }: { value: string; onChange: (v: string) => void; onFocus?: () => void; autoFocus?: boolean }) {
    return (
        <label className="flex items-center gap-2 rounded-xl bg-gray-100 px-3.5 py-2.5">
            <input
                value={value}
                autoFocus={autoFocus}
                onChange={(e) => onChange(e.target.value)}
                onFocus={onFocus}
                placeholder="Trouver une réponse"
                className="min-w-0 flex-1 bg-[transparent] text-sm text-ink placeholder:text-ink-muted focus:outline-none"
            />
            <Search className="h-4 w-4 shrink-0 text-ink" />
        </label>
    );
}

function ArticleRow({ article, onOpen, showExcerpt }: { article: HelpArticleRef; onOpen: () => void; showExcerpt?: boolean }) {
    return (
        <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 rounded-xl px-2 py-3 text-left transition-colors hover:bg-gray-100">
            <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">{article.title}</p>
                {showExcerpt && article.excerpt && <p className="mt-0.5 line-clamp-2 text-xs text-ink-muted">{article.excerpt}</p>}
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-ink" />
        </button>
    );
}

/** One message: ours on the left in grey, the client's on the right in ink, a meta line under each. */
function Bubble({ from, children, time, agent }: { from: "user" | "admin"; children: React.ReactNode; time?: string; agent?: string }) {
    const ours = from === "admin";
    const who = supportAgent(agent);
    return (
        <div className={`flex flex-col ${ours ? "items-start" : "items-end"}`}>
            <div
                className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    ours ? "bg-gray-100 text-ink" : "bg-surface-inverse text-ink-inverse"
                }`}
            >
                {children}
            </div>
            {ours ? (
                <span className="mt-1.5 flex items-center gap-2 text-xs text-ink-faint">
                    <Avatar size="xs" agent={who.key} />
                    {[who.name, time && relativeTime(time)].filter(Boolean).join(" • ")}
                </span>
            ) : (
                <span className="mt-1.5 pr-2 text-xs text-ink-faint">{time ? `Vous • ${relativeTime(time)}` : "Vous"}</span>
            )}
        </div>
    );
}

function Greeting() {
    const { online, backAt } = useSupportAvailability();
    return (
        <>
            <Bubble from="admin">{"Bonjour 👋\n\nVous parlez avec l'équipe DuckFlow. Comment pouvons-nous vous aider ?"}</Bubble>
            {!online && <Bubble from="admin">{offlineNotice(backAt)}</Bubble>}
        </>
    );
}

function Composer({ onSend, placeholder, autoFocus }: { onSend: (text: string) => Promise<boolean>; placeholder: string; autoFocus?: boolean }) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Grows with the text, up to max-h
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [text]);

    const send = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        if (await onSend(text.trim())) setText("");
        setSending(false);
    };

    const insertEmoji = (emoji: string) => {
        const el = inputRef.current;
        const start = el?.selectionStart ?? text.length;
        const end = el?.selectionEnd ?? text.length;
        setText(text.slice(0, start) + emoji + text.slice(end));
        setEmojiOpen(false);
        requestAnimationFrame(() => {
            el?.focus();
            el?.setSelectionRange(start + emoji.length, start + emoji.length);
        });
    };

    const canSend = !!text.trim() && !sending;

    return (
        <div className="relative px-4 pt-2">
            {emojiOpen && (
                <div className="absolute bottom-full left-4 right-4 mb-1 grid grid-cols-8 gap-1 rounded-2xl border border-border bg-surface p-2 shadow-float">
                    {EMOJIS.map((emoji) => (
                        <button key={emoji} type="button" onClick={() => insertEmoji(emoji)} className="rounded-lg p-1 text-xl hover:bg-gray-100">
                            {emoji}
                        </button>
                    ))}
                </div>
            )}
            <div className="rounded-2xl border border-border bg-surface px-3.5 pb-2.5 pt-2.5 transition-colors focus-within:border-border-strong">
                <textarea
                    ref={inputRef}
                    value={text}
                    autoFocus={autoFocus}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                    rows={1}
                    placeholder={placeholder}
                    className="block max-h-32 w-full resize-none bg-[transparent] text-sm text-ink placeholder:text-ink-faint focus:outline-none"
                />
                <div className="mt-2 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() => setEmojiOpen(!emojiOpen)}
                        aria-label="Emoji"
                        className={`rounded-lg p-1 transition-colors ${emojiOpen ? "text-ink" : "text-ink-muted hover:text-ink"}`}
                    >
                        <Smile className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={send}
                        disabled={!canSend}
                        aria-label="Envoyer"
                        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                            canSend ? "bg-surface-inverse text-ink-inverse hover:bg-ink" : "bg-gray-100 text-ink-faint"
                        }`}
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Thread({ ticket }: { ticket: SupportTicket }) {
    const endRef = useRef<HTMLDivElement>(null);
    useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [ticket.messages.length]);

    return (
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
            <Greeting />
            {ticket.messages.map((m) => (
                <Bubble key={m._id} from={m.from} time={m.createdAt} agent={m.agent}>
                    {m.body}
                </Bubble>
            ))}
            {ticket.status === "closed" && <p className="text-center text-xs text-ink-faint">Conversation clôturée. Écrivez-nous pour la rouvrir.</p>}
            <div ref={endRef} />
        </div>
    );
}

export default function SupportWidget({ visitor = false }: { visitor?: boolean }) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<Tab>("home");
    const [conv, setConv] = useState<Conversation | null>(null);
    const [tickets, setTickets] = useState<SupportTicketSummary[] | null>(null);
    const [ticket, setTicket] = useState<SupportTicket | null>(null);
    const [unread, setUnread] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [home, setHome] = useState<SupportHome | null>(null);

    // Help center
    const [help, setHelp] = useState<{ collections: HelpCollectionSummary[]; popular: HelpArticleRef[] } | null>(null);
    const [helpStack, setHelpStack] = useState<HelpView[]>([{ kind: "index" }]);
    const [helpQuery, setHelpQuery] = useState("");
    const [helpResults, setHelpResults] = useState<HelpArticleRef[] | null>(null);
    const [focusHelpSearch, setFocusHelpSearch] = useState(false);
    const [collection, setCollection] = useState<{ collection: { _id: string; title: string; description?: string }; articles: HelpArticleRef[] } | null>(null);
    const [article, setArticle] = useState<HelpArticleFull | null>(null);
    const helpView = helpStack[helpStack.length - 1];

    const [email, setEmail] = useState(() => readLocal(VISITOR_EMAIL_KEY));
    const [name, setName] = useState("");
    const [muted, setMuted] = useState(() => readLocal(SOUND_MUTED_KEY) === "1");

    // Read from the polling callbacks, which are not re-created on every render
    const mutedRef = useRef(muted);
    mutedRef.current = muted;
    const ticketRef = useRef(ticket);
    ticketRef.current = ticket;
    const watchingTicketRef = useRef(false);
    watchingTicketRef.current = open && conv?.kind === "ticket";
    const prevUnreadRef = useRef<number | null>(null);

    const chime = useCallback(() => {
        if (!mutedRef.current) playChime();
    }, []);

    const toggleMuted = () => {
        const next = !muted;
        setMuted(next);
        setMenuOpen(false);
        try {
            localStorage.setItem(SOUND_MUTED_KEY, next ? "1" : "0");
        } catch {
            // Private browsing: the choice lasts until the page is reloaded
        }
        if (!next) {
            unlockAudio();
            playChime();
        }
    };

    useEffect(() => {
        document.addEventListener("pointerdown", unlockAudio, { once: true });
        document.addEventListener("keydown", unlockAudio, { once: true });
        return () => {
            document.removeEventListener("pointerdown", unlockAudio);
            document.removeEventListener("keydown", unlockAudio);
        };
    }, []);

    const refreshUnread = useCallback(() => {
        if (document.visibilityState !== "visible") return;
        getMyUnreadCount()
            .then((count) => {
                // Not on the first read, and not while a conversation is open: its own polling chimes
                if (prevUnreadRef.current !== null && count > prevUnreadRef.current && !watchingTicketRef.current) chime();
                prevUnreadRef.current = count;
                setUnread(count);
            })
            .catch(() => {});
    }, [chime]);

    const loadTickets = useCallback(async () => {
        try {
            const list = await getMyTickets();
            setTickets(list);
            return list;
        } catch {
            setTickets([]);
            return [];
        }
    }, []);

    const openTicket = useCallback(
        async (id: string) => {
            setError(null);
            setMenuOpen(false);
            setTicket(null);
            setConv({ kind: "ticket", id });
            try {
                setTicket(await getMyTicket(id));
                refreshUnread();
            } catch (e) {
                setError(supportErrorMessage(e));
            }
        },
        [refreshUnread]
    );

    // Badge on the closed button
    useEffect(() => {
        refreshUnread();
        const timer = setInterval(refreshUnread, UNREAD_POLL_MS);
        return () => clearInterval(timer);
    }, [refreshUnread]);

    // Reply emails link to ?support=<ticketId>; help articles to ?support=new (a new conversation)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get("support");
        if (!id) return;
        params.delete("support");
        const qs = params.toString();
        window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
        setOpen(true);
        if (id === NEW_CONVERSATION_PARAM) {
            setConv({ kind: "new" });
            return;
        }
        openTicket(id);
    }, [openTicket]);

    // Each opening refreshes the home (team, unread) and the conversations; the help index once
    useEffect(() => {
        if (!open) return;
        getSupportHome().then(setHome).catch(() => {});
        loadTickets();
        if (!help) getHelpCenter().then(setHelp).catch(() => setHelp({ collections: [], popular: [] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Back to the list: show what changed while in the conversation
    useEffect(() => {
        if (open && !conv) loadTickets();
    }, [open, conv, loadTickets]);

    // Live-ish conversation
    useEffect(() => {
        if (!open || conv?.kind !== "ticket") return;
        const id = conv.id;
        const timer = setInterval(() => {
            if (document.visibilityState !== "visible") return;
            getMyTicket(id)
                .then((fresh) => {
                    const prev = ticketRef.current;
                    if (prev?._id === fresh._id && adminMessages(fresh) > adminMessages(prev)) chime();
                    setTicket(fresh);
                })
                .catch(() => {});
        }, TICKET_POLL_MS);
        return () => clearInterval(timer);
    }, [open, conv, chime]);

    // Help search, debounced
    useEffect(() => {
        const q = helpQuery.trim();
        if (q.length < 2) {
            setHelpResults(null);
            return;
        }
        const t = setTimeout(() => {
            searchHelp(q).then(setHelpResults).catch(() => setHelpResults([]));
        }, 250);
        return () => clearTimeout(t);
    }, [helpQuery]);

    // The help screen on top of the stack
    useEffect(() => {
        if (helpView.kind === "collection") {
            setCollection(null);
            getHelpCollection(helpView.id).then(setCollection).catch(() => setHelpStack([{ kind: "index" }]));
        } else if (helpView.kind === "article") {
            setArticle(null);
            getHelpArticle(helpView.id).then(setArticle).catch(() => setHelpStack([{ kind: "index" }]));
        }
    }, [helpView]);

    const startTicket = async (body: string) => {
        setError(null);
        const cleanEmail = email.trim().toLowerCase();
        if (visitor && !EMAIL_RX.test(cleanEmail)) {
            setError("Indiquez votre email pour recevoir notre réponse.");
            return false;
        }
        try {
            const created = await createTicket({
                body,
                page: window.location.pathname,
                ...(visitor ? { email: cleanEmail, name: name.trim() || undefined } : {}),
            });
            if (visitor) {
                try {
                    localStorage.setItem(VISITOR_EMAIL_KEY, cleanEmail);
                } catch {
                    // Private browsing: the email is just not remembered
                }
            }
            setTicket(created);
            setConv({ kind: "ticket", id: created._id });
            return true;
        } catch (e) {
            setError(supportErrorMessage(e));
            return false;
        }
    };

    const reply = async (body: string) => {
        if (conv?.kind !== "ticket") return false;
        setError(null);
        try {
            setTicket(await sendTicketMessage(conv.id, body));
            return true;
        } catch (e) {
            setError(supportErrorMessage(e));
            return false;
        }
    };

    const closeConversation = () => {
        setError(null);
        setMenuOpen(false);
        setTicket(null);
        setConv(null);
    };

    const newConversation = () => {
        setError(null);
        setMenuOpen(false);
        setTicket(null);
        setConv({ kind: "new" });
    };

    const openHelp = (view: HelpView, fresh = false) => {
        setTab("help");
        setHelpStack((stack) => (fresh ? [{ kind: "index" }, view] : [...stack, view]));
    };

    const helpBack = () => setHelpStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack));

    const close = () => {
        setMenuOpen(false);
        setOpen(false);
    };

    // Discreet way out of a conversation into the help center, only once there is something to read
    const helpShortcut = help && help.collections.length > 0 && (
        <p className="px-5 pb-1 text-center text-xs text-ink-muted">
            Besoin d'une réponse tout de suite ?{" "}
            <button
                type="button"
                onClick={() => {
                    closeConversation();
                    setHelpQuery("");
                    setHelpStack([{ kind: "index" }]);
                    setTab("help");
                }}
                className="font-medium text-ink underline underline-offset-2 hover:text-ink-muted"
            >
                Consultez nos articles d'aide
            </button>
        </p>
    );

    const availability = useSupportAvailability();

    // Most recent conversation, offered on the home when it has news or is still open
    const recent = tickets?.find((t) => t.unread) ?? tickets?.find((t) => t.status !== "closed") ?? null;

    const renderConversation = () => (
        <div className="flex flex-1 flex-col overflow-hidden">
            {/* Header */}
            <div className="relative flex items-center gap-2.5 border-b border-border px-3 py-3">
                <button type="button" onClick={closeConversation} aria-label="Retour" className="rounded-lg p-1 text-ink hover:bg-gray-100">
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <TeamAvatars onDuty={availability.onDuty} />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-base font-semibold text-ink">DuckFlow</p>
                    <p className="text-xs leading-snug text-ink-muted">
                        {availability.online ? "En ligne · l'équipe vous répond ici" : `Hors ligne · de retour ${availability.backAt}`}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Plus d'options"
                    aria-expanded={menuOpen}
                    className="rounded-lg p-1.5 text-ink-muted hover:bg-gray-100 hover:text-ink"
                >
                    <MoreHorizontal className="h-4 w-4" />
                </button>
                <button type="button" onClick={close} aria-label="Fermer" className="rounded-lg p-1.5 text-ink-muted hover:bg-gray-100 hover:text-ink">
                    <X className="h-4 w-4" />
                </button>

                {menuOpen && (
                    <div className="absolute right-12 top-full z-10 mt-1 w-56 rounded-2xl border border-border bg-surface p-1.5 shadow-float">
                        <button type="button" onClick={toggleMuted} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-xs text-ink hover:bg-gray-100">
                            {muted ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            {muted ? "Activer le son" : "Couper le son"}
                        </button>
                        {conv?.kind !== "new" && (
                            <button type="button" onClick={newConversation} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-xs text-ink hover:bg-gray-100">
                                <MessageCircle className="h-4 w-4" />
                                Nouvelle conversation
                            </button>
                        )}
                    </div>
                )}
            </div>

            {conv?.kind === "new" ? (
                <>
                    <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
                        <Greeting />
                        {visitor && (
                            <div className="flex flex-col items-start">
                                <div className="w-[85%] space-y-3 rounded-3xl bg-gray-100 px-4 py-3">
                                    <p className="text-sm text-ink">Pour recevoir notre réponse, laissez-nous votre email :</p>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Votre email"
                                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-border-strong focus:outline-none"
                                    />
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Votre nom (facultatif)"
                                        className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-xs text-ink placeholder:text-ink-faint focus:border-border-strong focus:outline-none"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {error && <p className="px-5 pb-1 text-xs text-danger">{error}</p>}
                    {helpShortcut}
                    <Composer onSend={startTicket} placeholder="Posez une question…" autoFocus={!visitor} />
                </>
            ) : (
                <>
                    {ticket ? (
                        <Thread ticket={ticket} />
                    ) : (
                        <div className="flex flex-1 items-center justify-center">{!error && <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />}</div>
                    )}
                    {error && <p className="px-5 pb-1 text-xs text-danger">{error}</p>}
                    {ticket && !availability.online && <p className="px-5 pb-1 text-xs text-ink-muted">{offlineNotice(availability.backAt)}</p>}
                    {ticket && helpShortcut}
                    {ticket && <Composer onSend={reply} placeholder={ticket.status === "closed" ? "Rouvrir la conversation…" : "Posez une question…"} autoFocus />}
                </>
            )}
            <PoweredBy />
        </div>
    );

    const renderHome = () => (
        <div className="relative flex-1 overflow-y-auto">
            {/* Dark top, a clean edge under the greeting (a fade to white read as a blur) */}
            <div className="absolute inset-x-0 top-0 h-64 bg-surface-inverse" aria-hidden />
            <div className="relative px-5 pb-5 pt-6">
                <div className="flex items-start">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface p-1">
                        <img src={logo} alt="DuckFlow" className="h-full w-full object-contain" />
                    </div>
                    <div className="ml-auto">
                        <TeamAvatars size="lg" onDuty={availability.onDuty} ring="ring-surface-inverse" />
                    </div>
                    <button type="button" onClick={close} aria-label="Fermer" className="ml-4 mt-2.5 rounded-lg p-1 text-ink-inverse hover:text-ink-inverse-muted">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-12">
                    <p className="text-2xl font-semibold leading-tight text-ink-inverse-muted">Bonjour{home?.firstName ? ` ${home.firstName}` : ""}</p>
                    <p className="text-2xl font-semibold leading-tight text-ink-inverse">En quoi pouvons-nous vous être utile ?</p>
                </div>

                <div className="mt-6 space-y-2.5">
                    {recent && (
                        <button
                            type="button"
                            onClick={() => openTicket(recent._id)}
                            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-left shadow-card transition-colors hover:bg-gray-100"
                        >
                            <Avatar size="sm" agent={recent.lastMessage?.from === "admin" ? recent.lastMessage.agent : undefined} />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-ink">{recent.unread ? "Nouvelle réponse" : "Conversation en cours"}</p>
                                <p className="truncate text-xs text-ink-muted">{recent.lastMessage?.body ?? recent.subject}</p>
                            </div>
                            {recent.unread && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-accent" aria-label="Non lu" />}
                            <ChevronRight className="h-4 w-4 shrink-0 text-ink" />
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={newConversation}
                        className="flex w-full items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-left shadow-card transition-colors hover:bg-gray-100"
                    >
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-ink">Poser une question</p>
                            <p className="text-sm text-ink-muted">
                                {availability.online ? "Notre équipe est en ligne et vous répond ici" : `Hors ligne · réponse ${availability.backAt}`}
                            </p>
                        </div>
                        <SendHorizontal className="h-4 w-4 shrink-0 fill-current text-ink" />
                    </button>

                    {help && help.collections.length > 0 && (
                        <div className="rounded-2xl border border-border bg-surface p-2 shadow-card">
                            <div className="p-1">
                                <HelpSearchBox
                                    value=""
                                    onChange={(v) => {
                                        setHelpQuery(v);
                                        setHelpStack([{ kind: "index" }]);
                                        setFocusHelpSearch(true);
                                        setTab("help");
                                    }}
                                    onFocus={() => {
                                        setHelpStack([{ kind: "index" }]);
                                        setFocusHelpSearch(true);
                                        setTab("help");
                                    }}
                                />
                            </div>
                            {help.popular.map((a) => (
                                <ArticleRow key={a._id} article={a} onOpen={() => openHelp({ kind: "article", id: a._id }, true)} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderMessages = () => (
        <div className="flex flex-1 flex-col overflow-hidden">
            <TabHeader title="Messages" onClose={close} />
            <div className="flex-1 overflow-y-auto border-t border-border">
                {tickets === null ? (
                    <div className="flex h-full items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
                        <MessageSquareText className="h-7 w-7 text-ink" />
                        <p className="text-sm font-semibold text-ink">Aucun message</p>
                        <p className="text-xs text-ink-muted">Les messages de l'équipe s'afficheront ici</p>
                    </div>
                ) : (
                    tickets.map((t) => (
                        <button
                            key={t._id}
                            type="button"
                            onClick={() => openTicket(t._id)}
                            className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-gray-100"
                        >
                            <Avatar size="sm" agent={t.lastMessage?.from === "admin" ? t.lastMessage.agent : undefined} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className={`truncate text-sm text-ink ${t.unread ? "font-semibold" : "font-medium"}`}>{t.subject}</p>
                                    {t.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-accent" aria-label="Nouvelle réponse" />}
                                </div>
                                <p className="truncate text-xs text-ink-muted">
                                    {t.lastMessage ? `${t.lastMessage.from === "admin" ? supportAgent(t.lastMessage.agent).name : "Vous"} • ${relativeTime(t.lastMessageAt)}` : ""}
                                    {t.status === "closed" ? " • Clôturée" : ""}
                                </p>
                            </div>
                            <ChevronRight className="h-4 w-4 shrink-0 text-ink" />
                        </button>
                    ))
                )}
            </div>
            <div className="flex justify-center px-4 pb-3 pt-3">
                <button
                    type="button"
                    onClick={newConversation}
                    className="inline-flex items-center gap-2 rounded-full bg-surface-inverse px-5 py-3 text-xs font-semibold text-ink-inverse transition-colors hover:bg-ink"
                >
                    Envoyez-nous un message
                    <Send className="h-4 w-4" />
                </button>
            </div>
        </div>
    );

    const renderHelp = () => {
        const searching = helpView.kind === "index" && helpQuery.trim().length >= 2;
        return (
            <div className="flex flex-1 flex-col overflow-hidden">
                <TabHeader title="Aide" onBack={helpStack.length > 1 ? helpBack : undefined} onClose={close} />
                {helpView.kind !== "article" && (
                    <div className="px-4 pb-3">
                        <HelpSearchBox
                            value={helpQuery}
                            autoFocus={focusHelpSearch || !!helpQuery}
                            onChange={(v) => {
                                setHelpQuery(v);
                                if (helpView.kind !== "index") setHelpStack([{ kind: "index" }]);
                            }}
                        />
                    </div>
                )}
                <div className="flex-1 overflow-y-auto border-t border-border">
                    {searching ? (
                        <div className="px-3 py-2">
                            {helpResults === null ? (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />
                                </div>
                            ) : helpResults.length === 0 ? (
                                <div className="px-3 py-8 text-center">
                                    <p className="text-sm text-ink">Aucun résultat</p>
                                    <button type="button" onClick={newConversation} className="mt-2 text-xs font-medium text-ink underline underline-offset-2">
                                        Posez votre question à l'équipe
                                    </button>
                                </div>
                            ) : (
                                helpResults.map((a) => <ArticleRow key={a._id} article={a} showExcerpt onOpen={() => openHelp({ kind: "article", id: a._id })} />)
                            )}
                        </div>
                    ) : helpView.kind === "index" ? (
                        help === null ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />
                            </div>
                        ) : help.collections.length === 0 ? (
                            <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
                                <HelpCircle className="h-7 w-7 text-ink" />
                                <p className="text-sm font-semibold text-ink">Le centre d'aide arrive bientôt</p>
                                <button type="button" onClick={newConversation} className="text-xs font-medium text-ink underline underline-offset-2">
                                    Posez votre question à l'équipe
                                </button>
                            </div>
                        ) : (
                            <>
                                <p className="border-b border-border px-4 py-4 text-base font-semibold text-ink">{plural(help.collections.length, "collection")}</p>
                                <div className="px-5">
                                    {help.collections.map((c) => (
                                        <button
                                            key={c._id}
                                            type="button"
                                            onClick={() => openHelp({ kind: "collection", id: c._id })}
                                            className="flex w-full items-center gap-3 border-b border-border py-3 text-left"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-ink">{c.title}</p>
                                                {c.description && <p className="truncate text-xs text-ink-muted">{c.description}</p>}
                                                <p className="text-sm text-ink-muted">{plural(c.articleCount, "article")}</p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 shrink-0 text-ink" />
                                        </button>
                                    ))}
                                </div>
                            </>
                        )
                    ) : helpView.kind === "collection" ? (
                        !collection ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />
                            </div>
                        ) : (
                            <>
                                <div className="border-b border-border px-4 py-4">
                                    <p className="text-base font-semibold text-ink">{collection.collection.title}</p>
                                    <p className="mt-2 text-sm text-ink-muted">{plural(collection.articles.length, "article")}</p>
                                    {collection.collection.description && <p className="text-sm text-ink-muted">{collection.collection.description}</p>}
                                </div>
                                <div className="px-3 py-2">
                                    {collection.articles.map((a) => (
                                        <ArticleRow key={a._id} article={a} onOpen={() => openHelp({ kind: "article", id: a._id })} />
                                    ))}
                                </div>
                            </>
                        )
                    ) : !article ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-4 w-4 animate-spin text-ink-faint" />
                        </div>
                    ) : (
                        <div className="px-5 py-5">
                            <h2 className="text-xl font-semibold leading-tight text-ink">{article.title}</h2>
                            <p className="mb-6 mt-2 text-xs text-ink-faint">
                                Mis à jour le {new Date(article.updatedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                            </p>
                            {/* A link to ?support=new opens the conversation right here instead of reloading the page */}
                            <div
                                onClick={(e) => {
                                    const href = (e.target as HTMLElement).closest("a")?.getAttribute("href") ?? "";
                                    if (new URLSearchParams(href.split("?")[1] ?? "").get("support") !== NEW_CONVERSATION_PARAM) return;
                                    e.preventDefault();
                                    newConversation();
                                }}
                            >
                                <HelpMarkdown source={article.body} />
                            </div>
                            <div className="mt-8 rounded-2xl bg-gray-100 px-4 py-3">
                                <p className="text-sm font-semibold text-ink">Vous n'avez pas trouvé votre réponse ?</p>
                                <button type="button" onClick={newConversation} className="mt-1 text-sm text-ink underline underline-offset-2">
                                    Posez votre question à l'équipe
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const TABS: { key: Tab; label: string; icon: typeof Home; badge?: number }[] = [
        { key: "home", label: "Accueil", icon: Home },
        { key: "messages", label: "Messages", icon: MessageSquareText, badge: unread },
        { key: "help", label: "Aide", icon: HelpCircle },
    ];

    return (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
            {open && (
                <div
                    role="dialog"
                    aria-label="Support DuckFlow"
                    className="mb-3 flex h-[min(640px,calc(100vh-6.5rem))] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl bg-surface shadow-float"
                >
                    {conv ? (
                        renderConversation()
                    ) : (
                        <>
                            {tab === "home" && renderHome()}
                            {tab === "messages" && renderMessages()}
                            {tab === "help" && renderHelp()}
                            <PoweredBy />
                            <nav aria-label="Support" className="flex border-t border-border">
                                {TABS.map(({ key, label, icon: Icon, badge }) => {
                                    const active = tab === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => {
                                                if (key === "help" && tab === "help") setHelpStack([{ kind: "index" }]);
                                setFocusHelpSearch(false);
                                                setTab(key);
                                            }}
                                            className={`relative flex flex-1 flex-col items-center gap-0.5 py-2 text-xs transition-colors ${
                                                active ? "font-semibold text-ink" : "text-ink-muted hover:text-ink"
                                            }`}
                                        >
                                            <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 1.75} />
                                            {label}
                                            {!!badge && (
                                                <span className="absolute left-1/2 top-2 ml-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1 text-xs font-semibold text-surface">
                                                    {badge > 9 ? "9+" : badge}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </nav>
                        </>
                    )}
                </div>
            )}

            <button
                type="button"
                onClick={() => {
                    if (!open) {
                        setError(null);
                        setConv(null);
                        setTicket(null);
                        setTab("home");
                    }
                    setMenuOpen(false);
                    setOpen(!open);
                }}
                aria-label={open ? "Fermer le support" : "Contacter le support"}
                className="relative flex h-12 w-12 items-center justify-center rounded-full bg-surface-inverse text-ink-inverse shadow-float transition-transform hover:scale-105"
            >
                {open ? <ChevronDown className="h-6 w-6" /> : <LauncherIcon />}
                {!open && unread > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1 text-xs font-semibold text-surface">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>
        </div>
    );
}
